import { useEffect } from "react";
import { MediaPreview } from "@/components/cms-puck/blocks/MediaPreview";
import { useSelectionCategoryPreview } from "@/context/cms-puck/SelectionCategoryPreviewContext";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  selectionCategoryPlainTitle,
  type SelectionCategory,
} from "@/lib/cms-puck/blocks/selection";

function CategoryVignette({
  attachmentId,
}: {
  attachmentId?: string | null;
}) {
  const id = attachmentId?.trim() || undefined;
  if (!id) {
    return (
      <div
        className="h-7 w-7 shrink-0 rounded bg-slate-100"
        aria-hidden
      />
    );
  }
  return (
    <div className="h-7 w-7 shrink-0 overflow-hidden rounded bg-slate-100">
      <MediaPreview
        attachmentId={id}
        size="petit"
        className="h-full w-full object-cover"
      />
    </div>
  );
}

function categoryLabel(
  cat: SelectionCategory,
  fallback: string,
): string {
  return selectionCategoryPlainTitle(cat.title) || fallback;
}

/**
 * Sélecteur de rubrique d'aperçu (éditeur uniquement) pour le bloc
 * `SelectionCategoryDetail`. Défaut = première rubrique disponible.
 * Affiche la vignette de chaque rubrique à côté du titre.
 */
export function SelectionCategoryPreviewField({
  value,
  onChange,
}: {
  value: string | null | undefined;
  onChange: (next: string | null) => void;
}) {
  const cmsNew = useCmsNew();
  const { categories, category, isLoading } = useSelectionCategoryPreview();
  const fallbackTitle = cmsNew(
    "cms.new.preview.selectionSections.category",
    "Catégorie",
  );

  // Si aucune valeur / invalide → ancrer la 1re rubrique dans les props.
  useEffect(() => {
    if (!category) return;
    const current = typeof value === "string" && value.trim() ? value.trim() : null;
    if (!current || !categories.some((c) => c.id === current)) {
      if (current !== category.id) onChange(category.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync défaut une fois catégories prêtes
  }, [category?.id, categories.length]);

  if (isLoading) {
    return (
      <p className="p-1 text-xs text-slate-400">
        {cmsNew("cms.new.auto.text.chargement", "Chargement…")}
      </p>
    );
  }

  if (categories.length === 0) {
    return (
      <p className="p-1 text-xs text-slate-500">
        {cmsNew(
          "cms.new.hint.selectionCategoryPreview.empty",
          "Aucune rubrique trouvée. Créez des catégories dans un bloc « Sélection — catégories ».",
        )}
      </p>
    );
  }

  const selected = value && categories.some((c) => c.id === value)
    ? value
    : category?.id ?? categories[0].id;
  const selectedCat =
    categories.find((c) => c.id === selected) ?? categories[0];

  return (
    <div className="space-y-1.5 p-1">
      <p className="text-xs text-slate-500">
        {cmsNew(
          "cms.new.hint.selectionCategoryPreview",
          "Aperçu éditeur : choisissez la rubrique simulée. Sur le site, le contenu vient de la rubrique cliquée.",
        )}
      </p>
      <Select
        value={selected}
        onValueChange={(id) => onChange(id)}
      >
        <SelectTrigger className="h-10 w-full bg-white">
          <SelectValue
            placeholder={cmsNew(
              "cms.new.pickers.selection.chooseCategory",
              "Choisir une rubrique",
            )}
          >
            <span className="flex min-w-0 items-center gap-2">
              <CategoryVignette
                attachmentId={selectedCat.thumbnailAttachmentId}
              />
              <span className="truncate">
                {categoryLabel(selectedCat, fallbackTitle)}
              </span>
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {categories.map((c) => (
            <SelectItem key={c.id} value={c.id} className="py-1.5">
              <span className="flex min-w-0 items-center gap-2">
                <CategoryVignette attachmentId={c.thumbnailAttachmentId} />
                <span className="truncate">
                  {categoryLabel(c, fallbackTitle)}
                </span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
